// r24 — TH province × NAATI page (77 provinces). Uniqueness = neighbour set + region-specific context.
import { Link } from "@tanstack/react-router";
import HubLeadCTA from "@/components/HubLeadCTA";
import NaatiAuthorByline from "@/components/NaatiAuthorByline";
import NaatiTripleKeyLinks from "@/components/NaatiTripleKeyLinks";
import NaatiSchemaGraph from "@/components/NaatiSchemaGraph";
import type { NaatiProvince } from "@/data/naati-th-provinces";
import { getNaatiProvinceNeighbours } from "@/data/naati-th-provinces";

export interface NaatiThProvinceRendererProps {
  locale: "th" | "en";
  province: NaatiProvince;
}

// Region-specific angle keyed by regionEn (from notary-coverage taxonomy).
const REGION_ANGLE_TH: Record<string, string> = {
  "Bangkok & Vicinity": "กรุงเทพและปริมณฑล — Partner 820 และ Student 500 มาแรงที่สุด ทะเบียนสมรส/สูติบัตร/ทรานสคริปต์คือชุดยอดฮิต",
  "North": "ภาคเหนือมีลูกค้าไทย-ต่างชาติเยอะ (เชียงใหม่-เชียงราย) เน้นทะเบียนสมรสข้ามชาติและ Retirement Visa สำหรับปลายทางออสฯ",
  "Northeast (Isaan)": "อีสาน (ขอนแก่น/อุดรฯ) เน้น Partner Visa 309/820 + ใบสำคัญเปลี่ยนชื่อ — เราจัดคอลเลกชันแบบ door-to-DHL",
  "Central": "ภาคกลาง — เอกสารทะเบียนสมรส/สูติบัตรสำหรับ Partner Visa และเอกสารการศึกษาสำหรับ Student 500",
  "East": "ภาคตะวันออก (ชลบุรี/ระยอง) เน้นวิศวกร EPC ไปออสฯ ต้องใช้ทรานสคริปต์ + Engineers Australia CDR",
  "West": "ภาคตะวันตก (กาญจนบุรี/เพชรบุรี) เอกสารทะเบียนสมรส + Bangkok Consulate transit",
  "South": "ภาคใต้ (ภูเก็ต/หาดใหญ่) เน้น hospitality-worker visa 482 + Diploma ของโรงเรียนโรงแรม",
};
const REGION_ANGLE_EN: Record<string, string> = {
  "Bangkok & Vicinity": "Bangkok Metro clients dominate Partner 820 and Student 500 — birth cert, marriage cert, and transcripts are the top three.",
  "North": "Chiang Mai/Chiang Rai see a high volume of mixed-nationality marriages and Australian retirement pathways.",
  "Northeast (Isaan)": "Isan (Khon Kaen/Udon) — Partner 309/820 flow with frequent name-change certificates; we run a door-to-DHL pickup.",
  "Central": "Central-plain clients mostly need marriage/birth certificates for Partner visas and education docs for Student 500.",
  "East": "Chonburi/Rayong — EPC engineers heading to Australia need transcripts plus Engineers Australia CDR support.",
  "West": "Kanchanaburi/Phetchaburi — marriage certificates plus a Bangkok Consulate transit step.",
  "South": "Phuket/Hat Yai — hospitality-worker visa 482 pathway with hotel-school diplomas.",
};

export default function NaatiThProvinceRenderer({ locale, province }: NaatiThProvinceRendererProps) {
  const isTh = locale === "th";
  const home = isTh ? "/translate/naati" : "/en/translate/naati";
  const hub = isTh ? "/translate/naati/city" : "/en/translate/naati/city";
  const name = isTh ? province.th : province.en;
  const angle = (isTh ? REGION_ANGLE_TH : REGION_ANGLE_EN)[province.regionEn] ??
    (isTh ? "ครอบคลุมทุกอำเภอ — ส่งเอกสาร DHL Domestic 1–2 วันทำการ" : "Full-province coverage, DHL Domestic 1–2 business days.");
  const neighbours = getNaatiProvinceNeighbours(province.slug, 6);

  const path = isTh ? `/translate/naati/city/${province.slug}` : `/en/translate/naati/city/${province.slug}`;
  const title = isTh
    ? `แปล NAATI ${name} — รับเอกสารถึงบ้าน ส่งกลับ 3 วันทำการ`
    : `NAATI Translation in ${name} — door pickup, 3-business-day turnaround`;
  const desc = isTh
    ? `บริการแปล NAATI Certified สำหรับลูกค้าใน${name} (${province.region}) — DHL Domestic 1–2 วันทำการ ราคาเริ่ม ฿1,300/หน้า`
    : `NAATI Certified translation for clients in ${name} (${province.regionEn}) — DHL Domestic 1–2 business days, from THB 1,300/page.`;

  return (
    <main className="mx-auto max-w-4xl px-5 py-12 sm:px-8 sm:py-16">
      <NaatiSchemaGraph
        locale={locale}
        path={path}
        title={title}
        description={desc}
        areaName={province.en}
        areaRegion={province.regionEn}
        areaCountry="TH"
        crumbs={[
          { name: isTh ? "หน้าแรก" : "Home", path: isTh ? "/" : "/en" },
          { name: "NAATI", path: home },
          { name: isTh ? "จังหวัด" : "Provinces", path: hub },
          { name, path },
        ]}
      />
      <nav className="mb-6 text-xs text-slate-500" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-amber-700">{isTh ? "หน้าแรก" : "Home"}</Link>
        <span className="mx-2">/</span>
        <Link to={home as any} className="hover:text-amber-700">NAATI</Link>
        <span className="mx-2">/</span>
        <Link to={hub as any} className="hover:text-amber-700">{isTh ? "จังหวัด" : "Provinces"}</Link>
      </nav>

      <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700" data-speakable="true">
        {isTh ? `แปล NAATI สำหรับลูกค้าใน${name}` : `NAATI Translation for clients in ${name}`}
      </div>
      <h1 className="mt-2 font-display text-3xl font-extrabold text-navy-deep sm:text-5xl" data-speakable="true">
        {isTh
          ? `แปล NAATI ${name} — รับเอกสารถึงบ้าน ส่งกลับ 3 วันทำการ`
          : `NAATI Translation in ${name} — door pickup, 3-business-day turnaround`}
      </h1>

      <section className="mt-8 rounded-2xl border border-amber-200 bg-amber-50/60 p-5">
        <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">
          {isTh ? "จุดเด่นของภูมิภาคนี้" : "Regional angle"}
        </div>
        <p className="mt-2 text-base font-medium text-navy-deep direct-answer" data-speakable="true">
          {angle}
        </p>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">
          {isTh ? `ทำงานอย่างไรกับลูกค้าใน${name}` : `How we work with clients in ${name}`}
        </h2>
        <ol className="mt-4 space-y-3 text-sm text-slate-700">
          <li><strong>1.</strong> {isTh ? "ส่งสแกน/รูปเอกสารทาง LINE @nycli — ทีมเช็คคุณภาพภายใน 30 นาที" : "Send scans via LINE @nycli — quality check within 30 minutes."}</li>
          <li><strong>2.</strong> {isTh ? "โอน 50% เพื่อล็อกคิวนักแปล NAATI Certified — จะได้ CPN ล่วงหน้าเช็คใน NAATI Directory" : "Pay 50% to lock a NAATI Certified translator — you receive the CPN in advance to check the NAATI Directory."}</li>
          <li><strong>3.</strong> {isTh ? "รับ PDF NAATI เต็มรูปแบบภายใน 3 วันทำการ + Hard copy DHL 1–2 วัน" : "Full NAATI PDF within 3 business days; hard copy by DHL 1–2 days."}</li>
          <li><strong>4.</strong> {isTh ? "หาก DHA / รัฐ BDM แจ้งแก้ — revise ฟรี 100%" : "Free 100% revision if DHA or a state BDM requests a correction."}</li>
        </ol>
      </section>

      <section className="mt-12">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">
          {isTh ? "ราคาเริ่มต้น" : "Starting prices"}
        </h2>
        <ul className="mt-4 space-y-2 text-sm text-slate-700">
          <li>• {isTh ? "แปลเอกสาร civil ทั่วไป: ฿1,300 / หน้า" : "Standard civil documents: THB 1,300 / page"}</li>
          <li>• {isTh ? "แปลทรานสคริปต์/ปริญญา: ฿1,500 / หน้า" : "Transcripts / degrees: THB 1,500 / page"}</li>
          <li>• {isTh ? "แปลเอกสารกฎหมาย / สัญญา: ฿1,800 / หน้า" : "Legal / contracts: THB 1,800 / page"}</li>
          <li>• {isTh ? "DHL Domestic ในไทย: ฿150–300" : "Domestic DHL inside Thailand: THB 150–300"}</li>
        </ul>
      </section>

      {neighbours.length > 0 && (
        <section className="mt-12">
          <h2 className="font-display text-xl font-extrabold text-navy-deep">
            {isTh ? `จังหวัดใกล้เคียง (${province.region})` : `Nearby provinces (${province.region})`}
          </h2>
          <ul className="mt-4 flex flex-wrap gap-2">
            {neighbours.map((n) => (
              <li key={n.slug}>
                <Link
                  to={(isTh ? `/translate/naati/city/${n.slug}` : `/en/translate/naati/city/${n.slug}`) as any}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-700 hover:border-amber-400 hover:text-amber-700"
                >
                  {isTh ? n.th : n.en}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <NaatiAuthorByline lang={isTh ? "th" : "en"} />

      <NaatiTripleKeyLinks
        mode={isTh ? "th" : "en"}
        countryNameTh="ออสเตรเลีย"
        countryNameEn="Australia"
      />

      <HubLeadCTA
        locale={isTh ? "th" : "en"}
        service="NAATI Translation"
        title={isTh ? `พร้อมสั่งแปล NAATI จาก${name}?` : `Ready to order NAATI from ${name}?`}
      />
    </main>
  );
}
